import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Buildings, Stack, Package, MagnifyingGlass, CaretDown, X } from "@phosphor-icons/react";

const NODE_ICON = { block: Buildings, floor: Stack, item: Package };

/**
 * BOQItemPicker — searchable dropdown for selecting a leaf BOQ item.
 * Loads /boq/tree, flattens leaf items, groups them by block>floor for display.
 *
 * Props:
 *   projectId (required)
 *   value: string | null (selected boq_item_id)
 *   onChange: (id | null, item | null) => void
 *   required?: boolean
 *   label?: string
 *   testId?: string
 */
export default function BOQItemPicker({ projectId, value, onChange, required = false, label = "BOQ Item", testId = "boq-item-picker" }) {
  const [items, setItems] = useState([]); // [{id, name, category, unit, block, floor}]
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!projectId) { setItems([]); return; }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/boq/tree", { params: { project_id: projectId } });
        const flat = [];
        const walk = (nodes, blk, flr) => {
          nodes.forEach((n) => {
            if (n.node_type === "item") {
              flat.push({
                id: n.id, name: n.name, category: n.category, unit: n.unit,
                description: n.description || "",
                rate: n.rate ?? null,
                total_qty: n.total_qty ?? null,
                block: blk, floor: flr,
                over_budget: n.over_budget,
                percent_ordered: n.percent_ordered,
              });
            } else if (n.node_type === "block") {
              walk(n.children || [], n.name, null);
            } else if (n.node_type === "floor") {
              walk(n.children || [], blk, n.name);
            }
          });
        };
        walk(data.nodes || [], null, null);
        if (alive) setItems(flat);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [projectId]);

  const selected = items.find((i) => i.id === value) || null;

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.toLowerCase();
    return items.filter((i) =>
      (i.name || "").toLowerCase().includes(needle) ||
      (i.category || "").toLowerCase().includes(needle) ||
      (i.block || "").toLowerCase().includes(needle) ||
      (i.floor || "").toLowerCase().includes(needle) ||
      (i.unit || "").toLowerCase().includes(needle)
    );
  }, [items, q]);

  return (
    <div data-testid={testId}>
      <Label className="text-xs uppercase tracking-wider text-neutral-500">
        {label} {required && <span className="text-[#C5221F]">*</span>}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-full h-10 border border-neutral-300 rounded-sm px-3 flex items-center justify-between text-sm bg-white hover:border-primary focus:outline-none focus:ring-1 focus:ring-primary text-left"
            data-testid={`${testId}-trigger`}
          >
            {selected ? (
              <span className="flex items-center gap-1.5 min-w-0">
                <Package size={13} weight="fill" className="text-[#137333] flex-shrink-0" />
                <span className="truncate">
                  <span className="text-neutral-500">{[selected.block, selected.floor].filter(Boolean).join(" > ")}{selected.block || selected.floor ? " › " : ""}</span>
                  <span className="text-neutral-900">{selected.name}</span>
                  <span className="text-neutral-400 text-[11px]"> · {selected.unit}</span>
                </span>
              </span>
            ) : (
              <span className="text-neutral-400">Select a BOQ item…</span>
            )}
            <span className="flex items-center gap-1 flex-shrink-0">
              {selected && (
                <span
                  role="button"
                  tabIndex={0}
                  className="text-neutral-400 hover:text-[#C5221F] p-0.5 cursor-pointer inline-flex items-center"
                  onClick={(e) => { e.stopPropagation(); onChange(null, null); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onChange(null, null); } }}
                  data-testid={`${testId}-clear`}
                >
                  <X size={12} />
                </span>
              )}
              <CaretDown size={12} className="text-neutral-400" />
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[560px] rounded-sm" data-testid={`${testId}-content`} align="start">
          <div className="p-2 border-b border-neutral-200">
            <div className="flex items-center gap-1 border border-neutral-200 rounded-sm px-2 h-8">
              <MagnifyingGlass size={12} className="text-neutral-400" />
              <Input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by block, floor, item, or category…"
                className="border-0 h-full px-1 text-sm focus-visible:ring-0"
                data-testid={`${testId}-search`}
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-xs text-neutral-400">Loading BOQ items…</div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-xs text-neutral-400">
                {items.length === 0 ? "No BOQ items yet in this project. Add some in Bill of Quantities first." : "No matches."}
              </div>
            ) : (
              filtered.map((it) => {
                const isSel = it.id === value;
                return (
                  <button
                    type="button"
                    key={it.id}
                    onClick={() => { onChange(it.id, it); setOpen(false); }}
                    className={`w-full text-left px-3 py-2 border-b border-neutral-100 hover:bg-neutral-50 flex items-center gap-2 ${isSel ? "bg-primary/5" : ""}`}
                    data-testid={`${testId}-option-${it.id}`}
                  >
                    <Package size={13} weight="fill" className="text-[#137333] flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-neutral-900 truncate">{it.name}</div>
                      <div className="text-[11px] text-neutral-500 truncate">
                        {[it.block, it.floor].filter(Boolean).join(" > ")}
                        {(it.block || it.floor) && <span> · </span>}
                        <span>{it.category} · {it.unit}</span>
                        {it.percent_ordered != null && <span className={`ml-2 ${it.over_budget ? "text-[#C5221F]" : "text-neutral-400"}`}>{it.percent_ordered}% ordered</span>}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
